import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  Icon,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';
import { IconArrowRightCircleFilled, IconHeartOutline } from '@zillow/constellation-icons';

import { RMWContext } from '../../../providers/RMWContext';
import emailUtils from '../../../utils/emailUtils';
import { PropertiesSelectors, UserSelectors } from '../../../selectors';
import FixIncorrectInformation from './components/index';

const CHOICES = {
  INCORRECT: {
    id: 'INCORRECT',
    text: 'My listing has incorrect or outdated information.',
  },
  SOURCE: {
    id: 'SOURCE',
    text: 'I want to change my listing source.',
  },
  SUPPORT: {
    id: 'SUPPORT',
    text: 'Neither of these. I need to contact support.',
  },
};

const ListingIssuesModalTrigger = ({ showIcon = true }) => {
  const { paths } = useContext(RMWContext);
  const { listingAlias, isMultifamily } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const { isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer } = useSelector((state) =>
    UserSelectors.makeUser(state),
  );
  const [choiceId, setChoiceId] = useState(null);
  const [showFixIncorrectInformation, setShowFixIncorrectInformation] = useState(false);

  const handleSubmit = () => {
    if (choiceId === CHOICES.INCORRECT.id) {
      setShowFixIncorrectInformation(true);
    } else if (choiceId === CHOICES.SOURCE.id) {
      window.location.href = `/rental-manager/properties/${listingAlias}/sources`;
    } else {
      const email = emailUtils.getHelpEmail(isMultifamily && isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer);
      window.location.href = `mailto:${email}`;
    }
  };

  const heading = showFixIncorrectInformation ? 'Fix incorrect or outdated information' : 'Listing issues';

  const renderNextButton = () => {
    return (
      <Button emphasis="filled" disabled={choiceId === null} onClick={handleSubmit}>
        Next
      </Button>
    );
  };

  return (
    <Modal
      header={
        <Heading level={1} textStyle="heading-xs">
          {heading}
        </Heading>
      }
      body={
        <>
          {showFixIncorrectInformation ? (
            <FixIncorrectInformation />
          ) : (
            <>
              <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconHeartOutline />} />
              <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>How can we help you?</Paragraph>
              <Box css={{ paddingBottom: '400' }}>
                {Object.keys(CHOICES).map((choiceKey) => {
                  const choice = CHOICES[choiceKey];
                  return (
                    <LabeledControl
                      key={choice.id}
                      controlId={`LabeledControl-choice-${choice.id}`}
                      label={<Label>{choice.text}</Label>}
                      control={
                        <Radio
                          name={`LabeledControl-choice-${choice.id}`}
                          checked={choice.id === choiceId}
                          onChange={() => setChoiceId(choice.id)}
                        />
                      }
                    />
                  );
                })}
              </Box>
            </>
          )}
        </>
      }
      footer={
        !showFixIncorrectInformation && (
          <ButtonGroup>
            <Modal.Close>
              <TextButton>Cancel</TextButton>
            </Modal.Close>
            {choiceId === CHOICES.INCORRECT.id ? renderNextButton() : <Modal.Close>{renderNextButton()}</Modal.Close>}
          </ButtonGroup>
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
      trigger={
        <TextButton
          css={{ marginBottom: showIcon ? '200' : '600' }}
          {...(showIcon
            ? { icon: <Icon size="md" render={<IconArrowRightCircleFilled />} />, iconPosition: 'end' }
            : {})}
        >
          Listing issues?
        </TextButton>
      }
    />
  );
};

export { ListingIssuesModalTrigger };
export default ListingIssuesModalTrigger;
